import { coerceTimeFormat } from "@app/_helpers/utils";
import { ComegoTime, FeedEntry, Time } from "@timeghost/web-api";
import { addMinutes, startOfDay } from "date-fns/esm";
import { CalendarDayData } from "../timer-calendar-view.service";
import {
	ProjecthourRowViewModel,
	ProjecthourSegmentViewModel,
	ScheduleTime,
	SegmentLayoutContext,
	SegmentRangeFill,
	WorkinghourSegmentsResult,
	WorkinghourSegmentViewModel,
} from "./timer-calendar-view.models";

interface SegmentPaintCandidate<TMeta> {
	intervalStart: Date;
	intervalEnd: Date;
	order: number;
	meta: TMeta;
}

interface ProjectSegmentCandidateMeta {
	state: "feed" | "project";
	feedRef?: FeedEntry;
	timeRef?: Time;
}

interface WorkinghourSegmentCandidateMeta {
	typeClass: string;
	comegoRef?: ComegoTime;
}

export function getTotalSegmentCount(ctx: SegmentLayoutContext): number {
	return (ctx.dayEndHour - ctx.dayStartHour) * ctx.segmentsPerHour;
}

export function isEvenHourSegment(seg: number, segmentsPerHour: number): boolean {
	return Math.floor(seg / segmentsPerHour) % 2 === 0;
}

export function resolveIndexStartTime(date: Date, index: number, dayStartHour: number, segmentsPerHour: number): Date {
	const segmentDuration = 60 / segmentsPerHour;
	const dayStart = startOfDay(date);
	return addMinutes(dayStart, dayStartHour * 60 + index * segmentDuration);
}

export function resolveIndexEndTime(date: Date, index: number, dayStartHour: number, segmentsPerHour: number): Date {
	const segmentDuration = 60 / segmentsPerHour;
	const dayStart = startOfDay(date);
	return addMinutes(dayStart, dayStartHour * 60 + (index + 1) * segmentDuration);
}

function createProjecthourSegmentGrid(ctx: SegmentLayoutContext): ProjecthourSegmentViewModel[] {
	const total = getTotalSegmentCount(ctx);
	return Array.from({ length: total }, (_, index) => ({
		index,
		state: "empty" as const,
		classes: {
			"segment-even": isEvenHourSegment(index, ctx.segmentsPerHour),
			"segment-odd": !isEvenHourSegment(index, ctx.segmentsPerHour),
		},
		isEven: isEvenHourSegment(index, ctx.segmentsPerHour),
	}));
}

function createWorkinghourSegmentGrid(ctx: SegmentLayoutContext): WorkinghourSegmentViewModel[] {
	const total = getTotalSegmentCount(ctx);
	return Array.from({ length: total }, (_, index) => ({
		index,
		isEmpty: true,
		classes: {
			"segment-even": isEvenHourSegment(index, ctx.segmentsPerHour),
			"segment-odd": !isEvenHourSegment(index, ctx.segmentsPerHour),
		},
		isEven: isEvenHourSegment(index, ctx.segmentsPerHour),
	}));
}

function intervalsOverlapInTime(
	intervalStartA: Date,
	intervalEndA: Date,
	intervalStartB: Date,
	intervalEndB: Date
): boolean {
	return intervalStartA < intervalEndB && intervalStartB < intervalEndA;
}

function segmentOverlapsTime(segStart: Date, segEnd: Date, timeStart: Date, timeEnd: Date): boolean {
	return segStart < timeEnd && segEnd > timeStart;
}

function comegoTypeToSegmentClass(type: ComegoTime["type"]): string | null {
	switch (type) {
		case "work":
			return "work";
		case "pause":
			return "break";
		case "absence":
			return "absence";
		default:
			return null;
	}
}

function workinghourTypeToTranslationKey(typeClass: string): string | null {
	switch (typeClass) {
		case "work":
			return "schedules.dashboard.workTime";
		case "break":
			return "comego.types.pause";
		case "absence":
			return "schedules.dashboard.absence";
		case "overtime":
			return "schedules.dashboard.overtime";
		case "should":
			return "schedules.dashboard.should";
		case "missed":
			return "schedules.dashboard.missing";
		default:
			return null;
	}
}

function getRowFillBounds(
	ctx: SegmentLayoutContext,
	day: Date,
	intervalStart: Date,
	intervalEnd: Date
): { leftPx: number; widthPx: number } {
	const total = getTotalSegmentCount(ctx);
	const rowStart = resolveIndexStartTime(day, 0, ctx.dayStartHour, ctx.segmentsPerHour);
	const rowEnd = resolveIndexEndTime(day, total - 1, ctx.dayStartHour, ctx.segmentsPerHour);
	const rowDurationMs = rowEnd.getTime() - rowStart.getTime();
	if (rowDurationMs <= 0) return { leftPx: 0, widthPx: 0 };

	const clampedStart = Math.max(intervalStart.getTime(), rowStart.getTime());
	const clampedEnd = Math.min(intervalEnd.getTime(), rowEnd.getTime());
	if (clampedEnd <= clampedStart) return { leftPx: 0, widthPx: 0 };

	const fractionStart = (clampedStart - rowStart.getTime()) / rowDurationMs;
	const fractionEnd = (clampedEnd - rowStart.getTime()) / rowDurationMs;

	return {
		leftPx: fractionStart * ctx.timelineWidthPx,
		widthPx: (fractionEnd - fractionStart) * ctx.timelineWidthPx,
	};
}

function getOverlappingSegmentRangeForInterval(
	ctx: SegmentLayoutContext,
	day: Date,
	timeStart: Date,
	timeEnd: Date
): { first: number; last: number } | null {
	const total = getTotalSegmentCount(ctx);
	let first: number | null = null;
	let last: number | null = null;

	for (let seg = 0; seg < total; seg++) {
		const segStart = resolveIndexStartTime(day, seg, ctx.dayStartHour, ctx.segmentsPerHour);
		const segEnd = resolveIndexEndTime(day, seg, ctx.dayStartHour, ctx.segmentsPerHour);
		if (!segmentOverlapsTime(segStart, segEnd, timeStart, timeEnd)) continue;
		if (first === null) first = seg;
		last = seg;
	}

	return first === null || last === null ? null : { first, last };
}

function collectProjecthourCandidates(
	ctx: SegmentLayoutContext,
	day: CalendarDayData
): SegmentPaintCandidate<ProjectSegmentCandidateMeta>[] {
	const candidates: SegmentPaintCandidate<ProjectSegmentCandidateMeta>[] = [];
	let order = 0;
	const nonBookedFeedEntries = day.projecthoursTimes.feedEntries?.filter((feed) => !feed.booked) ?? [];
	for (const feed of nonBookedFeedEntries) {
		const feedStart = new Date(feed.start);
		const feedEnd = new Date(feed.end);
		if (!getOverlappingSegmentRangeForInterval(ctx, day.date, feedStart, feedEnd)) continue;
		candidates.push({
			intervalStart: feedStart,
			intervalEnd: feedEnd,
			order: order++,
			meta: { state: "feed", feedRef: feed },
		});
	}

	for (const time of day.projecthoursTimes.times ?? []) {
		if (!time.end) continue;
		const timeStart = new Date(time.start);
		const timeEnd = new Date(time.end);
		if (!getOverlappingSegmentRangeForInterval(ctx, day.date, timeStart, timeEnd)) continue;
		candidates.push({
			intervalStart: timeStart,
			intervalEnd: timeEnd,
			order: order++,
			meta: { state: "project", timeRef: time },
		});
	}

	return candidates;
}

function assignProjecthourCandidatesToRows(
	candidates: SegmentPaintCandidate<ProjectSegmentCandidateMeta>[]
): SegmentPaintCandidate<ProjectSegmentCandidateMeta>[][] {
	const sorted = [...candidates].sort((a, b) => {
		const durationA = a.intervalEnd.getTime() - a.intervalStart.getTime();
		const durationB = b.intervalEnd.getTime() - b.intervalStart.getTime();
		if (durationB !== durationA) return durationB - durationA;
		return a.order - b.order;
	});

	const rows: SegmentPaintCandidate<ProjectSegmentCandidateMeta>[][] = [];

	for (const candidate of sorted) {
		let placed = false;
		for (const row of rows) {
			const overlaps = row.some((existing) =>
				intervalsOverlapInTime(
					candidate.intervalStart,
					candidate.intervalEnd,
					existing.intervalStart,
					existing.intervalEnd
				)
			);
			if (!overlaps) {
				row.push(candidate);
				placed = true;
				break;
			}
		}
		if (!placed) {
			rows.push([candidate]);
		}
	}

	return rows;
}

function buildProjecthourRangeFill(
	ctx: SegmentLayoutContext,
	day: CalendarDayData,
	candidate: SegmentPaintCandidate<ProjectSegmentCandidateMeta>
): SegmentRangeFill | null {
	const range = getOverlappingSegmentRangeForInterval(ctx, day.date, candidate.intervalStart, candidate.intervalEnd);
	if (!range) return null;

	const bounds = getRowFillBounds(ctx, day.date, candidate.intervalStart, candidate.intervalEnd);
	const labelAsTooltip = range.last - range.first + 1 <= 2;

	if (candidate.meta.state === "feed") {
		return {
			...bounds,
			rangeFirst: range.first,
			rangeLast: range.last,
			classes: { feed: true },
			style: { backgroundColor: "#ffeb3b" },
			label: candidate.meta.feedRef?.name || null,
			labelAsTooltip,
			feedRef: candidate.meta.feedRef,
		};
	}

	const color = ctx.getProjectColor(candidate.meta.timeRef?.project?.id);
	const projectColor = color ?? "#94a3b8";
	return {
		...bounds,
		rangeFirst: range.first,
		rangeLast: range.last,
		classes: { project: true },
		style: { backgroundColor: projectColor, borderColor: projectColor },
		label: candidate.meta.timeRef?.name || candidate.meta.timeRef?.project?.name || null,
		labelAsTooltip,
		timeRef: candidate.meta.timeRef ?? null,
	};
}

function buildProjecthourRow(
	ctx: SegmentLayoutContext,
	day: CalendarDayData,
	candidates: SegmentPaintCandidate<ProjectSegmentCandidateMeta>[]
): ProjecthourRowViewModel {
	const segments = createProjecthourSegmentGrid(ctx);
	const rangeFills = candidates
		.map((candidate) => buildProjecthourRangeFill(ctx, day, candidate))
		.filter((fill): fill is SegmentRangeFill => fill !== null);

	return { segments, rangeFills };
}

export function buildProjecthourSegments(ctx: SegmentLayoutContext, day: CalendarDayData): ProjecthourRowViewModel[] {
	const candidates = collectProjecthourCandidates(ctx, day);
	const rowCandidates = assignProjecthourCandidatesToRows(candidates);

	if (!rowCandidates.length) {
		return [{ segments: createProjecthourSegmentGrid(ctx), rangeFills: [] }];
	}

	return rowCandidates.map((row) => buildProjecthourRow(ctx, day, row));
}

function addWorkinghourCandidate(
	ctx: SegmentLayoutContext,
	candidates: SegmentPaintCandidate<WorkinghourSegmentCandidateMeta>[],
	day: Date,
	time: ComegoTime,
	typeClass: string,
	intervalStart: Date,
	intervalEnd: Date,
	order: number
): void {
	if (intervalEnd <= intervalStart) return;
	if (!getOverlappingSegmentRangeForInterval(ctx, day, intervalStart, intervalEnd)) return;

	candidates.push({
		intervalStart,
		intervalEnd,
		order,
		meta: { typeClass, comegoRef: time },
	});
}

function buildWorkinghourRangeFill(
	ctx: SegmentLayoutContext,
	day: CalendarDayData,
	candidate: SegmentPaintCandidate<WorkinghourSegmentCandidateMeta>
): SegmentRangeFill | null {
	const range = getOverlappingSegmentRangeForInterval(ctx, day.date, candidate.intervalStart, candidate.intervalEnd);
	if (!range) return null;

	const bounds = getRowFillBounds(ctx, day.date, candidate.intervalStart, candidate.intervalEnd);
	const labelAsTooltip = range.last - range.first + 1 <= 2;
	const translationKey = workinghourTypeToTranslationKey(candidate.meta.typeClass);

	return {
		...bounds,
		rangeFirst: range.first,
		rangeLast: range.last,
		classes: { [candidate.meta.typeClass]: true },
		style: {},
		labelKey: translationKey,
		labelAsTooltip,
		comegoRef: candidate.meta.comegoRef,
	};
}

function applyWorkinghourFillsToSegments(
	segments: WorkinghourSegmentViewModel[],
	rangeFills: SegmentRangeFill[]
): void {
	for (const fill of rangeFills) {
		if (fill.isSuggestion) {
			for (let seg = fill.rangeFirst; seg <= fill.rangeLast; seg++) {
				segments[seg] = { ...segments[seg], isSuggestion: true };
			}
			continue;
		}

		for (let seg = fill.rangeFirst; seg <= fill.rangeLast; seg++) {
			segments[seg] = { ...segments[seg], isEmpty: false };
		}
	}
}

export function buildWorkinghourSegments(ctx: SegmentLayoutContext, day: CalendarDayData): WorkinghourSegmentsResult {
	const segments = createWorkinghourSegmentGrid(ctx);
	const schedule = day.workinghoursTimes.schedule;
	const overTimeSec = Math.max(0, schedule.usage - schedule.cap);
	const latestWorkTime =
		(day.workinghoursTimes.times ?? [])
			.filter((t) => t.type === "work" && t.end)
			.sort((a, b) => new Date(b.end).getTime() - new Date(a.end).getTime())[0] ?? null;

	const candidates: SegmentPaintCandidate<WorkinghourSegmentCandidateMeta>[] = [];
	let order = 0;

	for (const time of day.workinghoursTimes.times ?? []) {
		const timeStart = new Date(time.start);
		const timeEnd = new Date(time.end);
		const typeClass = comegoTypeToSegmentClass(time.type);
		if (!typeClass) continue;

		if (time === latestWorkTime && overTimeSec > 0 && typeClass === "work") {
			const cutMs = Math.min(overTimeSec * 1000, timeEnd.getTime() - timeStart.getTime());
			const splitAt = new Date(timeEnd.getTime() - cutMs);
			addWorkinghourCandidate(ctx, candidates, day.date, time, "work", timeStart, splitAt, order++);
			addWorkinghourCandidate(ctx, candidates, day.date, time, "overtime", splitAt, timeEnd, order++);
		} else {
			addWorkinghourCandidate(ctx, candidates, day.date, time, typeClass, timeStart, timeEnd, order++);
		}
	}

	const rangeFills = candidates
		.map((candidate) => buildWorkinghourRangeFill(ctx, day, candidate))
		.filter((fill): fill is SegmentRangeFill => fill !== null);

	applyWorkinghourFillsToSegments(segments, rangeFills);

	const today = startOfDay(new Date());
	if (day.workinghoursTimes.schedule.schedule.enabled) {
		for (const scheduleItem of day.workinghoursTimes.schedule.schedule.items ?? []) {
			const scheduleStart = coerceTimeFormat((scheduleItem.from ?? scheduleItem.start) as string, day.date);
			const scheduleEnd = coerceTimeFormat((scheduleItem.to ?? scheduleItem.end) as string, day.date);
			const range = getOverlappingSegmentRangeForInterval(ctx, day.date, scheduleStart, scheduleEnd);
			if (!range) continue;

			const hasOverlap = segments.slice(range.first, range.last + 1).some((seg) => !seg.isEmpty);
			if (hasOverlap) continue;

			const typeClass = startOfDay(day.date) >= today ? "should" : "missed";
			const translationKey = workinghourTypeToTranslationKey(typeClass);
			const labelAsTooltip = range.last - range.first + 1 <= 2;
			const bounds = getRowFillBounds(ctx, day.date, scheduleStart, scheduleEnd);
			const scheduleRef: ScheduleTime = { start: scheduleStart, end: scheduleEnd, type: "work" };

			const suggestionFill: SegmentRangeFill = {
				...bounds,
				rangeFirst: range.first,
				rangeLast: range.last,
				classes: {
					[typeClass]: true,
					"suggestion-action": true,
				},
				style: {},
				labelKey: translationKey,
				labelAsTooltip,
				scheduleRef,
				isSuggestion: true,
			};

			rangeFills.push(suggestionFill);
			applyWorkinghourFillsToSegments(segments, [suggestionFill]);
		}
	}

	return { segments, rangeFills };
}

export function resolveProjecthourFillFromDomIndex(
	rows: ProjecthourRowViewModel[],
	rowIndex: number,
	fillDomIndex: number
): SegmentRangeFill | null {
	return rows[rowIndex]?.rangeFills[fillDomIndex] ?? null;
}
